import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  type ReactNode,
} from "react";

/**
 * Single WebSocket connection shared across the whole app. Instead of
 * each feature opening its own socket, consumers subscribe to frames
 * and filter by `kind`:
 *
 *   { kind: "chat", jobId, chunk }        — agent stream
 *   { kind: "sync_progress", jobId, progress } — sync job updates
 *
 * Reconnects with linear backoff. Authenticates via ?token=<idToken> on
 * the upgrade request (browsers can't send headers on WS upgrade).
 */

export type AppFrame = {
  kind?: string;
  [k: string]: unknown;
};

type Listener = (frame: AppFrame) => void;

interface WsContextValue {
  subscribe: (fn: Listener) => () => void;
}

const WsContext = createContext<WsContextValue>({
  subscribe: () => () => {},
});

interface WsProviderProps {
  endpoint: string | null;
  token: string | null;
  children: ReactNode;
}

export function WsProvider({ endpoint, token, children }: WsProviderProps) {
  const listenersRef = useRef<Set<Listener>>(new Set());

  const subscribe = useCallback((fn: Listener) => {
    listenersRef.current.add(fn);
    return () => {
      listenersRef.current.delete(fn);
    };
  }, []);

  useEffect(() => {
    if (!endpoint || !token) return;
    let cancelled = false;
    let retry = 0;
    let ws: WebSocket | null = null;

    const open = (): void => {
      if (cancelled) return;
      const url = `${endpoint}?token=${encodeURIComponent(token)}`;
      ws = new WebSocket(url);

      ws.onopen = () => {
        retry = 0;
      };
      ws.onmessage = (ev) => {
        let frame: AppFrame | null = null;
        try {
          frame = JSON.parse(ev.data) as AppFrame;
        } catch {
          return;
        }
        if (!frame) return;
        for (const fn of listenersRef.current) {
          try {
            fn(frame);
          } catch {
            /* ignore listener errors so one bad subscriber doesn't kill the rest */
          }
        }
      };
      ws.onclose = () => {
        if (cancelled) return;
        retry = Math.min(retry + 1, 6);
        setTimeout(open, 500 * retry);
      };
      ws.onerror = () => {
        /* onclose will fire and trigger reconnect */
      };
    };

    open();
    return () => {
      cancelled = true;
      ws?.close();
      ws = null;
    };
  }, [endpoint, token]);

  return <WsContext.Provider value={{ subscribe }}>{children}</WsContext.Provider>;
}

/**
 * Subscribe to all WS frames. Caller is responsible for filtering by kind.
 * Handlers are stored in a ref so re-renders don't re-subscribe.
 */
export function useWsFrames(onFrame: Listener): void {
  const { subscribe } = useContext(WsContext);
  const ref = useRef(onFrame);
  ref.current = onFrame;
  useEffect(() => subscribe((f) => ref.current(f)), [subscribe]);
}
